import type { ISODateString, SharePointId } from './timesheet.types';

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum OrderCostAllocation {
  ProjectCost  = 'Project Cost',
  HealthSafety = 'Health & Safety',
  Overhead     = 'Overhead',
  Capex        = 'Capex',
}

export enum OrderStatus {
  Draft                    = 'Draft',
  Requested                = 'Requested',
  /** @deprecated kept for backward-compat with existing SP data */
  LMApproved               = 'LM Approved',
  PendingEscalatedApproval = 'Pending Escalated Approval',
  PendingCEOApproval       = 'Pending CEO Approval',
  PendingCFOApproval       = 'Pending CFO Approval',
  PendingChemApproval      = 'Pending Chemical Approval',
  ChemicalApproved         = 'Chemical Approved',
  OMApproved               = 'OM Approved',
  Ordered                  = 'Ordered',
  PartialDelivery          = 'Partial Delivery',
  Delivered                = 'Delivered',
  ForReview                = 'For Review',
  Rejected                 = 'Rejected',
  Cancelled                = 'Cancelled',
}


export enum OrderCurrency {
  GBP = 'GBP',
  EUR = 'EUR',
  USD = 'USD',
  JPY = 'JPY',
  NZD = 'NZD',
  HUF = 'HUF',
}

export enum OrderPriority {
  Standard = 'Standard',
  Urgent   = 'Urgent',
  Low      = 'Low',
}

export enum ApprovalStage {
  SelfApproval      = 'Self-Approval',
  LineManager       = 'Line Manager',
  EscalatedApproval = 'Escalated Approval',
  CEOOnBehalfOf     = 'CEO On-Behalf-Of',
  CFO               = 'CFO',
  Chemical          = 'Chemical',
  OM                = 'OM',
}

export enum ApprovalDecision {
  Approved = 'Approved',
  Rejected = 'Rejected',
}

// ─── Domain Models ────────────────────────────────────────────────────────────

export interface Order {
  readonly id: SharePointId;
  readonly spId: number;
  title: string;
  orderRef: string;
  requesterEmail: string;
  requesterName: string;
  costAllocation: OrderCostAllocation;
  projectCode: string;
  projectName: string;
  supplier: string;
  priority: OrderPriority;
  currency: OrderCurrency;
  totalAmount: number;
  sterlingEquivalent: number | null;
  orderDate: ISODateString | null;
  requiredByDate: ISODateString | null;
  expectedDeliveryDate: ISODateString | null;
  actualDeliveryDate: ISODateString | null;
  status: OrderStatus;
  notes: string;
  poNumber: string;
  costCentreName: string;
  approvedOnBehalfOf: string;
  hasChemicalLines: boolean;
  isLocked: boolean;
  isSelfApproved: boolean;
  fyiViewerEmails: readonly string[];
  lineManagerEmail: string;
  lineManagerName: string;
  cfoApproverEmail: string;
  escalatedApproverEmail: string;
  escalatedApproverName: string;
  readonly created: string;
  readonly modified: string;
}

export interface OrderLineItem {
  readonly id: SharePointId;
  orderId: number;
  itemDescription: string;
  sku: string;
  itemLink: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  subtotal: number;
  isChemical: boolean;
  coshhRef: string;
  lineOrder: number;
}

export interface OrderApprovalLog {
  readonly id: SharePointId;
  orderId: number;
  approvalStage: ApprovalStage;
  approverName: string;
  approverEmail: string;
  decision: ApprovalDecision;
  note: string;
  onBehalfOf: string;
  approvalDate: string;
  attachmentRef: string;
}

export interface OrderComment {
  readonly id: SharePointId;
  orderId: number;
  authorName: string;
  authorEmail: string;
  commentText: string;
  commentDate: string;
}

export interface COSHHEntry {
  readonly id: SharePointId;
  coshhRef: string;
  chemicalName: string;
  hazardType: string;
  description: string;
}

export type AttachmentCategory = 'quote' | 'ceo-approval' | 'other';

export interface OrderAttachment {
  /** File name as stored in the document library (e.g. "quote_20240128_filename.pdf"). */
  name: string;
  /** Server-relative URL, e.g. "/sites/mysite/Order Attachments/ORD-0001/quote_...pdf". */
  serverRelUrl: string;
  /** ISO timestamp from SP TimeCreated. */
  uploadedAt: string;
  category: AttachmentCategory;
}

// ─── Write DTOs ───────────────────────────────────────────────────────────────

export interface NewOrderData {
  title: string;
  costAllocation: OrderCostAllocation;
  projectCode?: string;
  projectName?: string;
  supplier?: string;
  priority: OrderPriority;
  currency: OrderCurrency;
  totalAmount: number;
  sterlingEquivalent?: number | null;
  requiredByDate?: string | null;
  notes?: string;
  hasChemicalLines: boolean;
  lineManagerEmail: string;
  lineManagerName: string;
  requesterEmail: string;
  requesterName: string;
  status: OrderStatus;
}

export interface OrderData extends NewOrderData {
  orderRef: string;
  orderDate?: string | null;
  expectedDeliveryDate?: string | null;
  actualDeliveryDate?: string | null;
  poNumber?: string;
  costCentreName?: string;
  approvedOnBehalfOf?: string;
  isLocked?: boolean;
  isSelfApproved?: boolean;
  fyiViewerEmails?: string[];
  cfoApproverEmail?: string;
  escalatedApproverEmail?: string;
  escalatedApproverName?: string;
}

export interface NewLineItemData {
  orderId: number;
  itemDescription: string;
  sku: string;
  itemLink: string;
  quantity: number;
  unitOfMeasure: string;
  unitPrice: number;
  subtotal: number;
  isChemical: boolean;
  coshhRef: string;
  lineOrder: number;
}

export interface NewApprovalData {
  orderId: number;
  approvalStage: ApprovalStage;
  approverName: string;
  approverEmail: string;
  decision: ApprovalDecision;
  note: string;
  onBehalfOf?: string;
  attachmentRef?: string;
}

export interface NewCommentData {
  orderId: number;
  authorName: string;
  authorEmail: string;
  commentText: string;
}

export interface OrderFilters {
  requesterEmail?: string;
  status?: OrderStatus;
  fyiViewerEmail?: string;
}

// ─── UI helpers ───────────────────────────────────────────────────────────────

export const CURRENCY_SYMBOLS: Record<OrderCurrency, string> = {
  [OrderCurrency.GBP]: '£',
  [OrderCurrency.EUR]: '€',
  [OrderCurrency.USD]: '$',
  [OrderCurrency.JPY]: '¥',
  [OrderCurrency.NZD]: 'NZ$',
  [OrderCurrency.HUF]: 'Ft',
};

export function formatOrderAmount(amount: number, currency: OrderCurrency): string {
  const sym = CURRENCY_SYMBOLS[currency];
  return `${sym}${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Returns true when an order is past its expected delivery date and not yet delivered. */
export function isOverdue(order: Order): boolean {
  if (!order.expectedDeliveryDate) return false;
  if (order.status === OrderStatus.Delivered || order.status === OrderStatus.Cancelled) return false;
  return new Date(order.expectedDeliveryDate) < new Date(new Date().toDateString());
}
